"use client"

import { ColumnDef, RowData } from "@tanstack/react-table"
//import { Checkbox } from "@/Components/ui/checkbox"
import { format } from "date-fns"
import { dateformat } from "@/utils/GlobalConstants"

// This type is used to define the shape of our data.
// You can use a Zod schema here if you want.
export type UpcomingDeliverable = {
    id: number
    workstream: string
    workPackage: string
    deliverable: string
    status: "Complete" | "Not Complete"
    actualFinish?: Date
    plannedFinish: Date
    owner: string
}

declare module '@tanstack/react-table' {
    //allows us to define custom properties for our columns
    interface ColumnMeta<TData extends RowData, TValue> {
        filterVariant?: 'text' | 'date-range' | 'select' | 'bool-select'
    }
}

export function getUpcomingDeliverablesColumns(app: { selectedProject?: { projectTypeId: number } }): ColumnDef<UpcomingDeliverable>[] {
const columns: ColumnDef<UpcomingDeliverable>[] = [];

columns.push(
        {
            id: "id",
            accessorKey: "id",
            header: "ID",
            cell: ({ row }) => {
                const taskId = row.getValue("id");
                return <div className="text-center">{taskId as number}</div>
            },
            filterFn: (row, columnId, filterValue) => {
                // needs to match exact number
                const value = row.getValue(columnId) as number;
                const filterNumber = typeof filterValue === "string" ? parseInt(filterValue, 10) : filterValue;
                return value === filterNumber;
            },
            size: 50,
        });

if (app.selectedProject?.projectTypeId === 2) {

    columns.push({
        id: "workstream",
        accessorKey: "workstream",
        header: "WS",
        meta: {
            filterVariant: 'select',
        },
        cell: ({ row }) => {
            const workstream = row.getValue("workstream");
            return <div className="text-center">{workstream as string}</div>
        },
        size: 120,
    });
}

columns.push(
    {
        id: "workPackage",
        accessorKey: "workPackage",
        header: "WP",
        meta: {
            filterVariant: 'select',
        },
        //cell: ({ row }) => {
        //    const workPackage = row.getValue("workPackage");
        //    return <div className="pl-8">{workPackage as string}</div>
        //},
        size: 120,
    },
    {
        id: "deliverable",
        accessorKey: "deliverable",
        header: "Deliverable",
        size: 500,
    },
    {
        id: "actualFinish",
        accessorKey: "actualFinish",
        header: "Actual Finish",
        meta: {
            filterVariant: 'date-range',
        },
        cell: ({ row }) => {
            if (row.getValue("actualFinish") == null) return <div className="text-center">-</div>;

            const actualFinish = row.getValue("actualFinish");
            return <div className="text-center">{format((actualFinish as Date), dateformat)}</div>
        },
        filterFn: (row, columnId, filterValue) => {
            if (row.getValue(columnId) == null) return true; // if its null just return true as cant be filtered

            const value = new Date(row.getValue(columnId));
            const from = filterValue.from ? new Date(filterValue.from) : null;
            const to = filterValue.to ? new Date(filterValue.to) : null;
            if (!from || !to) return true;
            return value >= from && value <= to;
        },
        size: 110,
    },
    {
        id: "plannedFinish",
        accessorKey: "plannedFinish",
        header: "Planned Finish",
        meta: {
            filterVariant: 'date-range',
        },
        cell: ({ row }) => {
            const plannedFinish = new Date(row.getValue("plannedFinish"));
            const today = new Date();
            let style = "";
            if (plannedFinish.getTime() < today.getTime() && row.getValue("status") !== "Complete") {
                style = " text-red-500";
            }
            return <div className={"text-center" + style}>{format((plannedFinish as Date), dateformat)}</div>
        },
        filterFn: (row, columnId, filterValue) => {
            const value = new Date(row.getValue(columnId));
            const from = filterValue.from ? new Date(filterValue.from) : null;
            const to = filterValue.to ? new Date(filterValue.to) : null;
            if (!from || !to) return true;
            return value >= from && value <= to;
        },
        size: 110,
    },
    {
        id: "status",
        accessorKey: "status",
        header: "Status",
        meta: {
            filterVariant: 'select',
        },
        cell: ({ row }) => {
            const status = row.getValue("status");
            const style = status === "Complete" ? "bg-green-500" : "bg-gray-500";
            return <div className={"text-center text-white " + style}>{status as string}</div>
        },
        filterFn: (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            return value == filterValue;
        },
    },
    {
        id: "owner",
        accessorKey: "owner",
        header: "Deliverables Owner",
        cell: ({ row }) => {
            const owner = row.getValue("owner");
            return <div className="text-center">{owner as string}</div>
        },
    },
);
return columns;
}

export const columnDisplayNames: [string, string][] = [
    ["id", "ID"],
    ["workstream", "Workstream"],
    ["workPackage", "Work Package"],
    ["deliverable", "Deliverable"],
    ["actualFinish", "Actual Finish"],
    ["plannedFinish", "Planned Finish"],
    ["status", "Status"],
    ["owner", "Deliverables Owner"],
]